import { useEffect, useState, useCallback } from "react";
import {
  BarChart3,
  Eye,
  ShoppingBag,
  PlayCircle,
  Calendar,
  Globe,
  Smartphone,
  RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { api } from "../../lib/api";
import logger from "../../lib/logger";
import { toast } from "sonner";

const RANGES = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

// Module-level chart style constants — stable references, no per-render allocations.
const CHART_MARGIN = { top: 10, right: 10, left: 0, bottom: 0 };
const AXIS_TICK = { fontFamily: "monospace", fontSize: 11 };
const TOOLTIP_CONTENT_STYLE = {
  backgroundColor: "#1a1a1a",
  border: "1px solid rgba(255,255,255,0.1)",
  fontFamily: "monospace",
  fontSize: 12,
};
const TOOLTIP_LABEL_STYLE = { color: "#D21F1F" };
const formatXAxisTick = (d) => d.slice(5);

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-demolition-paper border border-white/10 p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="font-mono text-white/50 text-xs uppercase tracking-wider">{label}</p>
        <p className="font-russo text-4xl text-white mt-2">{value.toLocaleString()}</p>
      </div>
      <Icon className={`w-10 h-10 ${accent} opacity-60`} />
    </div>
  </div>
);

const TopList = ({ title, items, emptyText, testId }) => (
  <div className="bg-demolition-paper border border-white/10 p-6" data-testid={testId}>
    <h3 className="font-teko text-lg text-demolition-chrome uppercase tracking-widest mb-4">
      {title}
    </h3>
    {items.length === 0 ? (
      <p className="font-mono text-white/40 text-sm">{emptyText}</p>
    ) : (
      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between gap-4 py-2 border-b border-white/5 last:border-b-0"
          >
            <span className="font-mono text-sm text-white/80 truncate" title={item.label}>
              {item.label}
            </span>
            <span className="font-russo text-base text-demolition-red flex-shrink-0">
              {item.count.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function AdminAnalytics() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (rangeDays) => {
    setLoading(true);
    try {
      const summary = await api.getAnalyticsSummary(rangeDays);
      setData(summary);
    } catch (err) {
      logger.warn("Analytics fetch failed:", err.message);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(days);
  }, [days, fetchData]);

  const totals = data?.totals || {};
  const daily = data?.daily || [];
  const topPages = data?.top_pages || [];
  const topShop = data?.top_shop_clicks || [];
  const topEvents = data?.top_events || [];
  const topReferrers = data?.top_referrers || [];
  const countries = data?.countries || [];
  const devices = data?.devices || [];

  return (
    <div data-testid="admin-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
            ANALYTICS
          </h1>
          <p className="font-mono text-white/50 mt-2">
            Privacy-friendly site metrics — no cookies, no third parties
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="analytics-range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              data-testid={`range-${r.value}`}
              className={`px-4 py-2 font-teko text-base uppercase tracking-wider transition-colors border ${
                days === r.value
                  ? "bg-demolition-red text-white border-demolition-red"
                  : "border-white/20 text-white/70 hover:bg-white/5"
              }`}
            >
              {r.label}
            </button>
          ))}
          <button
            onClick={() => fetchData(days)}
            disabled={loading}
            className="p-2 border border-white/20 text-white/70 hover:bg-white/5 transition-colors disabled:opacity-50"
            data-testid="refresh-analytics"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="text-center py-12">
          <p className="font-mono text-white/50 animate-pulse">Loading analytics...</p>
        </div>
      )}

      {data && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Eye} label="Page Views" value={totals.pageviews || 0} accent="text-demolition-red" />
            <StatCard icon={ShoppingBag} label="Shop Clicks" value={totals.shop_clicks || 0} accent="text-yellow-500" />
            <StatCard icon={PlayCircle} label="DemoPod CTAs" value={totals.youtube_cta_clicks || 0} accent="text-red-500" />
            <StatCard icon={Calendar} label="Event Views" value={totals.event_views || 0} accent="text-blue-400" />
          </div>

          {/* Daily trend */}
          <div className="bg-demolition-paper border border-white/10 p-6 mb-8" data-testid="analytics-daily-chart">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="w-5 h-5 text-demolition-red" />
              <h3 className="font-teko text-lg text-demolition-chrome uppercase tracking-widest">
                Page Views — Last {days} Days
              </h3>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily} margin={CHART_MARGIN}>
                  <defs>
                    <linearGradient id="pvFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D21F1F" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#D21F1F" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="date"
                    stroke="rgba(255,255,255,0.4)"
                    tick={AXIS_TICK}
                    tickFormatter={formatXAxisTick}
                  />
                  <YAxis
                    stroke="rgba(255,255,255,0.4)"
                    tick={AXIS_TICK}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                  />
                  <Area type="monotone" dataKey="count" stroke="#D21F1F" strokeWidth={2} fill="url(#pvFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top lists row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <TopList
              title="Top Pages"
              items={topPages}
              emptyText="No page views yet."
              testId="top-pages"
            />
            <TopList
              title="Shop Link Clicks"
              items={topShop}
              emptyText="No shop clicks yet. Add some Shop items first!"
              testId="top-shop-clicks"
            />
          </div>

          {/* Top lists row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <TopList
              title="Top Events"
              items={topEvents}
              emptyText="No event detail views yet."
              testId="top-events"
            />
            <TopList
              title="Top Referrers"
              items={topReferrers}
              emptyText="Most visits are direct (no referrer)."
              testId="top-referrers"
            />
          </div>

          {/* Bottom row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-demolition-paper border border-white/10 p-6" data-testid="countries-card">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 text-demolition-red" />
                <h3 className="font-teko text-lg text-demolition-chrome uppercase tracking-widest">
                  Top Countries
                </h3>
              </div>
              {countries.length === 0 ? (
                <p className="font-mono text-white/40 text-sm">
                  Country data appears once visitors are routed via Cloudflare.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {countries.map((c) => (
                    <div
                      key={c.label}
                      className="bg-demolition-subtle border border-white/5 p-3 text-center"
                    >
                      <span className="font-russo text-lg text-white block">{c.label}</span>
                      <span className="font-mono text-xs text-white/50">{c.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-demolition-paper border border-white/10 p-6" data-testid="devices-card">
              <div className="flex items-center gap-3 mb-4">
                <Smartphone className="w-5 h-5 text-demolition-red" />
                <h3 className="font-teko text-lg text-demolition-chrome uppercase tracking-widest">
                  Devices
                </h3>
              </div>
              {devices.length === 0 ? (
                <p className="font-mono text-white/40 text-sm">No device data yet.</p>
              ) : (
                <div className="space-y-3">
                  {devices.map((d) => {
                    const total = devices.reduce((s, x) => s + x.count, 0) || 1;
                    const pct = Math.round((d.count / total) * 100);
                    return (
                      <div key={d.label}>
                        <div className="flex justify-between text-xs font-mono text-white/60 mb-1">
                          <span className="uppercase">{d.label}</span>
                          <span>{d.count} ({pct}%)</span>
                        </div>
                        <div className="h-2 bg-demolition-subtle">
                          <div
                            className="h-full bg-demolition-red"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
